import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, ChevronLeft, ChevronRight, Camera, Loader2, RefreshCw, Star, Trash2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface PropertyGalleryProps {
  mainImage: string | null;
  galleryUrls: string[];
  title: string;
  isVerified?: boolean;
  onSyncGallery?: () => Promise<void>;
  isSyncing?: boolean;
  isAdmin?: boolean;
  onSetAsHero?: (imageUrl: string) => void;
  onRemoveImage?: (imageUrl: string) => void;
}

const PropertyGallery = ({ mainImage, galleryUrls, title, isVerified, onSyncGallery, isSyncing, isAdmin, onSetAsHero, onRemoveImage }: PropertyGalleryProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allImages = [
    ...(mainImage ? [mainImage] : []),
    ...galleryUrls,
  ].filter(Boolean);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => setLightboxIndex((i) => (i + 1) % allImages.length);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + allImages.length) % allImages.length);

  const thumbnails = allImages.slice(1, 6);

  return (
    <>
      <div className="container mx-auto px-4 max-w-6xl mb-8">
        {/* Main image */}
        <div
          className="aspect-[16/10] md:aspect-[16/8] bg-muted relative rounded-2xl overflow-hidden cursor-pointer group"
          onClick={() => allImages.length > 0 && openLightbox(0)}
        >
          {allImages.length > 0 ? (
            <img
              src={allImages[0]}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Home className="w-16 h-16 text-muted-foreground/30" />
            </div>
          )}
          {isVerified && (
            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground px-3 py-1.5 rounded-full shadow-lg">
              ✓ Verified Direct
            </div>
          )}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-background/80 text-foreground px-3 py-1.5 rounded-full backdrop-blur-sm shadow-md">
              <Camera className="w-3.5 h-3.5" />
              {allImages.length} Photos
            </div>
          )}
        </div>

        {/* Thumbnail row */}
        {thumbnails.length > 0 ? (
          <div className="grid grid-cols-5 gap-2 mt-2">
            {thumbnails.map((url, i) => (
              <div key={i} className="relative group/thumb">
                <button
                  onClick={() => openLightbox(i + 1)}
                  className="aspect-[4/3] w-full rounded-xl overflow-hidden bg-muted border-2 border-transparent hover:border-primary/50 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <img
                    src={url}
                    alt={`${title} ${i + 2}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
                {isAdmin && (
                  <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                    {onSetAsHero && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onSetAsHero(url); }}
                        className="bg-background/90 text-foreground hover:bg-primary hover:text-primary-foreground rounded-full p-1.5 shadow-md backdrop-blur-sm"
                        title="Set as Hero image"
                      >
                        <Star className="w-3 h-3" />
                      </button>
                    )}
                    {onRemoveImage && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemoveImage(url); }}
                        className="bg-background/90 text-foreground hover:bg-destructive hover:text-destructive-foreground rounded-full p-1.5 shadow-md backdrop-blur-sm"
                        title="Remove image"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : mainImage ? (
          <div className="flex items-center gap-3 mt-3">
            {isSyncing ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Syncing gallery photos…</span>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={onSyncGallery}
                className="text-xs"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Sync Gallery Photos
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 border-0 bg-black/95 rounded-none sm:rounded-2xl overflow-hidden [&>button]:text-white [&>button]:hover:text-white/80">
          <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
            <AnimatePresence mode="wait">
              <motion.img
                key={lightboxIndex}
                src={allImages[lightboxIndex]}
                alt={`${title} ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              />
            </AnimatePresence>

            {allImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-12 w-12 rounded-full"
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 h-12 w-12 rounded-full"
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full backdrop-blur-sm">
              {lightboxIndex + 1} / {allImages.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyGallery;
